import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { FileBarChart2, Plus, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const FIELD_LABEL = {
  jumlah_ibadah: "Jumlah Ibadah",
  jumlah_hadir_rata2: "Rata-rata Kehadiran",
  baptisan: "Baptisan",
  sidi: "Sidi",
  keuangan_penerimaan: "Penerimaan (Rp)",
  catatan: "Catatan",
};

export default function Laporan() {
  const { user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [active, setActive] = useState("");
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [subForm, setSubForm] = useState({});
  const [newPeriodOpen, setNewPeriodOpen] = useState(false);
  const [newPeriod, setNewPeriod] = useState({ period: "", label: "", type: "LAPORAN_TRIWULAN", due_date: "" });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRow, setReviewRow] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");

  const canManage = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS"].includes(user?.role);
  const canReview = canManage;

  const loadPeriods = async () => {
    const { data } = await api.get("/report-periods");
    setPeriods(data);
    if (data.length && !active) setActive(data[0].period);
    return data;
  };
  const loadProgress = async (period) => {
    if (!period) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/report-periods/${period}/progress`);
      setProgress(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadPeriods(); }, []);
  useEffect(() => { if (active) loadProgress(active); }, [active]);

  const openSubmit = (row) => {
    setSelectedRow(row);
    const content = row.submission?.content || {};
    const initial = {};
    (progress?.period?.fields || []).forEach((f) => initial[f] = content[f] ?? "");
    setSubForm(initial);
    setSubmitOpen(true);
  };

  const submit = async () => {
    if (!selectedRow) return;
    try {
      const content = {};
      Object.entries(subForm).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        content[k] = (k === "catatan") ? v : Number(v);
      });
      await api.post("/report-submissions", {
        period: active, church_id: selectedRow.church.id, content, notes: null,
      });
      toast.success("Laporan berhasil dikirim.");
      setSubmitOpen(false); setSelectedRow(null);
      loadProgress(active);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const decide = async (decision) => {
    if (!reviewRow?.submission) return;
    try {
      await api.patch(`/report-submissions/${reviewRow.submission.id}/review`, { decision, notes: reviewNotes || null });
      toast.success(decision === "ACCEPTED" ? "Laporan diterima." : "Permintaan revisi terkirim.");
      setReviewOpen(false); setReviewRow(null); setReviewNotes("");
      loadProgress(active);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createPeriod = async () => {
    if (!newPeriod.period || !newPeriod.label || !newPeriod.due_date) { toast.error("Semua field wajib diisi."); return; }
    try {
      await api.post("/report-periods", newPeriod);
      toast.success("Periode laporan dibuat.");
      setNewPeriodOpen(false);
      await loadPeriods(); setActive(newPeriod.period);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rows = progress?.rows || [];
  const total = progress?.total || 0;
  const submitted = progress?.submitted || 0;
  const accepted = progress?.accepted || 0;
  const pct = total > 0 ? Math.round((submitted / total) * 100) : 0;
  const today = new Date().toISOString().slice(0,10);
  const isOverdue = progress?.period?.due_date && progress.period.due_date < today;

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Pelaporan</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Laporan Jemaat</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Periode pelaporan berjenjang: draft di jemaat → submit → review Klasis → rekap dashboard.</p>
        </div>
        <div className="flex items-center gap-2">
          {periods.length > 0 && (
            <Select value={active} onValueChange={setActive}>
              <SelectTrigger className="w-56" data-testid="select-lap-period"><SelectValue /></SelectTrigger>
              <SelectContent>{periods.map((p) => <SelectItem key={p.period} value={p.period}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {canManage && (
            <Dialog open={newPeriodOpen} onOpenChange={setNewPeriodOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="btn-new-lap-period"><Plus className="h-4 w-4 mr-2" /> Periode</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Periode Laporan Baru</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Kode periode</Label><Input value={newPeriod.period} onChange={(e)=>setNewPeriod({...newPeriod, period:e.target.value})} placeholder="2026-Q2" data-testid="input-lap-code" /></div>
                  <div><Label>Label</Label><Input value={newPeriod.label} onChange={(e)=>setNewPeriod({...newPeriod, label:e.target.value})} placeholder="Laporan Triwulan II 2026" data-testid="input-lap-label" /></div>
                  <div><Label>Batas waktu</Label><Input type="date" value={newPeriod.due_date} onChange={(e)=>setNewPeriod({...newPeriod, due_date:e.target.value})} data-testid="input-lap-due" /></div>
                </div>
                <DialogFooter><Button onClick={createPeriod} data-testid="btn-save-lap-period">Simpan</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : !progress ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center">
          <FileBarChart2 className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Belum ada periode laporan.</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Progres</div>
              <div className="font-heading font-bold text-3xl mt-1 text-primary" data-testid="lap-progress-count">
                {submitted}<span className="text-xl text-muted-foreground">/{total}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Sudah lapor ({pct}%)</div>
              <Progress value={pct} className="h-1.5 mt-2" />
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Diterima Klasis</div>
              <div className="font-heading font-bold text-3xl mt-1">{accepted}</div>
              <div className="text-xs text-muted-foreground mt-1">Sudah diverifikasi</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Belum Lapor</div>
              <div className="font-heading font-bold text-3xl mt-1 text-secondary">{progress.outstanding}</div>
              <div className="text-xs text-muted-foreground mt-1">Jemaat</div>
            </CardContent></Card>
            <Card className={isOverdue ? "border-destructive/40 bg-destructive/5" : ""}><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Batas Waktu</div>
              <div className="font-heading font-bold text-xl mt-1">{progress.period.due_date}</div>
              {isOverdue && <div className="text-xs text-destructive mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3"/> Sudah lewat</div>}
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Status per Jemaat</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Jemaat</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Dikirim</TableHead>
                    <TableHead>Direview</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const canSubmit = user && (
                      user.role === "SUPER_ADMIN" ||
                      ((user.role === "SEKRETARIS_JEMAAT" || user.role === "GEMBALA_JEMAAT") && user.church_id === r.church.id)
                    );
                    return (
                      <TableRow key={r.church.id} data-testid={`lap-row-${r.church.code}`}>
                        <TableCell>
                          <div className="font-medium text-sm">{r.church.name}</div>
                          <div className="text-xs text-muted-foreground">{r.church.code}</div>
                        </TableCell>
                        <TableCell>
                          {r.status === "BELUM_LAPOR" && <StatusBadge status="muted" label="BELUM LAPOR" />}
                          {r.status === "SUBMITTED" && <StatusBadge status="SUBMITTED" />}
                          {r.status === "ACCEPTED" && <StatusBadge status="success" label="DITERIMA" />}
                          {r.status === "REVISION_REQUIRED" && <StatusBadge status="REVISION_REQUIRED" />}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {r.submission?.submitted_at ? new Date(r.submission.submitted_at).toLocaleDateString("id-ID") : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {r.submission?.reviewed_at ? `${new Date(r.submission.reviewed_at).toLocaleDateString("id-ID")} — ${r.submission.reviewed_by}` : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {canSubmit && r.status !== "ACCEPTED" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs rounded-full" onClick={() => openSubmit(r)} data-testid={`btn-submit-lap-${r.church.code}`}>
                                <Send className="h-3 w-3 mr-1" /> {r.status === "BELUM_LAPOR" ? "Isi & Submit" : "Perbaiki"}
                              </Button>
                            )}
                            {canReview && r.status === "SUBMITTED" && (
                              <Button size="sm" className="h-7 text-xs rounded-full" onClick={() => { setReviewRow(r); setReviewOpen(true); }} data-testid={`btn-review-lap-${r.church.code}`}>
                                Review
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Submit dialog */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Laporan {selectedRow?.church?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {(progress?.period?.fields || []).map((f) => (
              <div key={f}>
                <Label>{FIELD_LABEL[f] || f}</Label>
                {f === "catatan" ? (
                  <Textarea rows={2} value={subForm[f] ?? ""} onChange={(e)=>setSubForm({...subForm, [f]: e.target.value})} data-testid={`input-lap-${f}`} />
                ) : (
                  <Input type="number" value={subForm[f] ?? ""} onChange={(e)=>setSubForm({...subForm, [f]: e.target.value})} data-testid={`input-lap-${f}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={submit} data-testid="btn-save-lap"><Send className="h-4 w-4 mr-2" /> Kirim Laporan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Review Laporan — {reviewRow?.church?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {reviewRow?.submission?.content && (
              <div className="border border-border rounded-md p-3 text-xs space-y-1 bg-muted/30">
                {Object.entries(reviewRow.submission.content).map(([k,v]) => (
                  <div key={k} className="flex justify-between"><span className="text-muted-foreground">{FIELD_LABEL[k] || k}</span><strong>{String(v)}</strong></div>
                ))}
              </div>
            )}
            <div><Label>Catatan review</Label><Textarea value={reviewNotes} onChange={(e)=>setReviewNotes(e.target.value)} data-testid="input-review-notes" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={()=>decide("REVISION_REQUIRED")} data-testid="btn-review-revision">Minta Revisi</Button>
            <Button onClick={()=>decide("ACCEPTED")} data-testid="btn-review-accept"><CheckCircle2 className="h-4 w-4 mr-2"/>Terima</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
